import React, { ChangeEvent, Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ILabel } from "@formant/core/model/ILabel";
import { ILabeledPolygon } from "@formant/core/model/ILabeledPolygon";
import { InputElement } from "@formant/ui/component/common/Field";
import { TextField } from "@formant/ui/component/common/TextField";

import { Form } from "../../common/Form";
import { IconButton } from "../../common/IconButton";
import * as styles from "./LabeledPolygonItem.scss";

export interface ILabeledPolygonItemProps {
    polygon: ILabeledPolygon;
    active: boolean;
    labels: ILabel[];
    readOnly: boolean;
    onDelete: () => void;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}

@observer
export class LabeledPolygonItem extends Component<ILabeledPolygonItemProps> {
    @observable
    private newLabel: string = "";

    public render() {
        const {
            polygon,
            active,
            labels,
            readOnly,
            onDelete,
            onMouseEnter,
            onMouseLeave,
        } = this.props;

        const labelDisplayName = (label: string) => {
            const found = labels.find(_ => _.value === label);
            return (found && found.displayName) || label;
        };

        return (
            <div
                className={classnames(
                    styles["labeled-polygon-item"],
                    active && styles.active,
                )}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <div className={styles.labels}>
                    {!readOnly && (
                        <Form onSubmit={this.onLabelAdded}>
                            <TextField
                                label="Label"
                                value={this.newLabel}
                                className={styles["new-label"]}
                                collapsed={true}
                                small={true}
                                placeholder="Add label"
                                list={labels.map(_ => _.displayName || _.value)}
                                autoFocus={true}
                                onChange={this.onChangeNewLabel}
                            />
                        </Form>
                    )}
                    {polygon.labels.map((label, j) => {
                        const onDeleteLabel = () => {
                            polygon.labels = polygon.labels.filter(
                                _ => _ !== label,
                            );
                        };
                        return (
                            <div key={j} className={styles.label}>
                                {labelDisplayName(label)}
                                {!readOnly && (
                                    <IconButton
                                        icon="close"
                                        className={styles["delete-label"]}
                                        onClicked={onDeleteLabel}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
                {!readOnly && (
                    <IconButton
                        icon="delete"
                        className={styles.delete}
                        onClicked={onDelete}
                    />
                )}
            </div>
        );
    }

    private onLabelAdded = async () => {
        if (!this.newLabel) {
            return;
        }

        const { polygon, labels } = this.props;

        const labelValue = (displayName: string) => {
            const found = labels.find(_ => _.displayName === displayName);
            return (found && found.value) || displayName;
        };

        const value = labelValue(this.newLabel);
        if (!polygon.labels.includes(value)) {
            polygon.labels.push(value);
        }

        this.newLabel = "";
    };

    private onChangeNewLabel = async (event: ChangeEvent<InputElement>) => {
        this.newLabel = event.currentTarget.value;
        if (
            this.props.labels.find(
                _ => (_.displayName || _.value) === this.newLabel,
            )
        ) {
            await this.onLabelAdded();
        }
    };
}
